import type { LocationSuggestion } from '~/services/api/community/types';

export const formatLocationSuggestion = (suggestion: LocationSuggestion): string =>
  [suggestion.name, suggestion.place_formatted].filter(Boolean).join(', ');

export const isNullableString = (value: unknown): value is string | null => value === null || typeof value === 'string';

export const hasValidCoordinates = (value: { latitude?: unknown; longitude?: unknown }): boolean =>
  typeof value.latitude === 'number' &&
  Number.isFinite(value.latitude) &&
  Math.abs(value.latitude) <= 90 &&
  typeof value.longitude === 'number' &&
  Number.isFinite(value.longitude) &&
  Math.abs(value.longitude) <= 180;

/** A picked suggestion, or `null` when the member typed over a pick and the stored row must go. */
export type GeolocationChange = LocationSuggestion | null;

/**
 * Serializes the picker's result for the form-encoded `__actions` request. `undefined` means the
 * member never touched the field, so nothing is sent and the stored row is left alone.
 */
export const geolocationField = (geolocation: GeolocationChange | undefined): Record<string, string> =>
  geolocation === undefined ? {} : { geolocation: JSON.stringify(geolocation ?? { _destroy: true }) };

type GeolocationAttributes =
  | { _destroy: true }
  | { latitude: number; longitude: number; country_code: string | null; region: string | null; city: string | null };

const isGeolocationAttributes = (value: unknown): value is GeolocationAttributes => {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Record<string, unknown>;
  if (record._destroy === true) return true;
  return (
    hasValidCoordinates(record) &&
    isNullableString(record.country_code) &&
    isNullableString(record.region) &&
    isNullableString(record.city)
  );
};

export const parseGeolocation = (raw: FormDataEntryValue | null): GeolocationAttributes | undefined => {
  if (raw === null) return undefined;
  try {
    const parsed: unknown = JSON.parse(String(raw));
    return isGeolocationAttributes(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};
